import React, { useState, useEffect } from 'react';
import supabase from '../config/supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
import '../index2.css';

const InsertGameEventsPage = () => {
  const [events, setEvents] = useState([]);
  const [playerSuggestions, setPlayerSuggestions] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const gameId = new URLSearchParams(location.search).get('gameId');

  useEffect(() => {
    const fetchSummonedPlayersAndEvents = async () => {
      try {
        // Fetch summoned players
        const { data: summonedPlayersData, error: playersError } = await supabase
          .from('call_players_game')
          .select('player_id, players(Name)')
          .eq('game_id', gameId);

        if (playersError) throw playersError;

        const summonedPlayers = (summonedPlayersData || []).map(item => ({
          id: item.player_id,
          Name: item.players?.Name || 'Unknown'
        }));
        setPlayerSuggestions(summonedPlayers);

        // Fetch existing game events
        const { data: existingEvents, error: eventsError } = await supabase
          .from('game_event')
          .select('*')
          .eq('game_id', gameId);

        if (eventsError) throw eventsError;

        if (existingEvents && existingEvents.length > 0) {
          setEvents(existingEvents.map(e => ({
            player_id: e.player_id,
            minute: e.minute,
            type: e.type,
            id: e.id // Keep the record id for update
          })));
        } else {
          // No previous records: initialize one empty row
          setEvents([{ player_id: '', minute: '', type: '' }]);
        }
      } catch (error) {
        setFetchError('Could not fetch data: ' + (error.message || 'Unknown error'));
      }
    };

    fetchSummonedPlayersAndEvents();
  }, [gameId]);

  const handleEventChange = (index, field, value) => {
    setEvents(prevEvents =>
      prevEvents.map((event, i) =>
        i === index ? { ...event, [field]: value } : event
      )
    );
  };

  const addEventRow = () => {
    setEvents([...events, { player_id: '', minute: '', type: '' }]);
  };

  const removeEventRow = (index) => {
    setEvents(events.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      for (let event of events) {
        const formattedEvent = {
          game_id: gameId,
          player_id: event.player_id,
          minute: parseInt(event.minute, 10),
          type: event.type
        };

        if (event.id) {
          // Update existing record
          const { error } = await supabase
            .from('game_event')
            .update(formattedEvent)
            .eq('id', event.id);

          if (error) throw error;
        } else {
          // Insert new record
          const { error } = await supabase
            .from('game_event')
            .insert([formattedEvent]);

          if (error) throw error;
        }
      }

      console.log('Events saved successfully');
      navigate(-1); // Go back after save
    } catch (error) {
      setFetchError('Could not save events: ' + (error.message || 'Unknown error'));
    }
  };

  return (
    <div className="page insert-game-events">
      <button onClick={() => navigate(-1)} className="back-button">Back</button>
      {fetchError && <p className="error">{fetchError}</p>}
      <h2>Insert Game Events</h2>
      <div className="events-form">
        {events.map((event, index) => (
          <div key={index} className="event-item">
            <label>
              Player:
              <input
                type="text"
                list={`player-suggestions-${index}`}
                value={event.player_id}
                onChange={(e) => handleEventChange(index, 'player_id', e.target.value)}
                placeholder="Select a player..."
              />
              <datalist id={`player-suggestions-${index}`}>
                {playerSuggestions.map(player => (
                  <option key={player.id} value={player.id}>{player.Name}</option>
                ))}
              </datalist>
            </label>
            <label>
              Minute:
              <input
                type="number"
                value={event.minute}
                onChange={(e) => handleEventChange(index, 'minute', e.target.value)}
                placeholder="Minute"
              />
            </label>
            <label>
              Type:
              <select
                value={event.type}
                onChange={(e) => handleEventChange(index, 'type', e.target.value)}
              >
                <option value="">Select type</option>
                <option value="yellow card">Yellow Card</option>
                <option value="red card">Red Card</option>
                <option value="goal">Goal</option>
              </select>
            </label>
            <button onClick={() => removeEventRow(index)} className="remove-button">Remove</button>
            <hr />
          </div>
        ))}
      </div>
      <button onClick={addEventRow} className="add-button">Add Another Event</button>
      <button onClick={handleSubmit} className="submit-button">Save Events</button>
    </div>
  );
};

export default InsertGameEventsPage;
